import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Mail, MessageSquare, Clock, CheckCircle2 } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    rotationId: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement actual form submission to backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>
              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            Get in <span className="text-[#4A90E2]">Touch</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Questions? Feedback? Found a bug? We're here to help.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-white mb-6">Send us a message</h2>
              
              {submitted && (
                <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold">Message sent!</p>
                    <p className="text-gray-300 text-sm">We'll get back to you within 24-48 hours.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white font-semibold mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#4A90E2] transition-colors"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white font-semibold mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#4A90E2] transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-white font-semibold mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#4A90E2] transition-colors"
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Question</option>
                    <option value="support">Technical Support</option>
                    <option value="billing">Billing Question</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                    <option value="grievance">Grievance Assistance</option>
                    <option value="partnership">Partnership Inquiry</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="rotationId" className="block text-white font-semibold mb-2">
                    Rotation ID <span className="text-gray-400 text-sm font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="rotationId"
                    name="rotationId"
                    value={formData.rotationId}
                    onChange={handleChange}
                    className="w-full bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#4A90E2] transition-colors"
                    placeholder="e.g., 4302 (if relevant to your question)"
                  />
                  <p className="text-gray-400 text-sm mt-1">
                    Include your rotation number if asking about a specific trip
                  </p>
                </div>

                <div>
                  <label htmlFor="message" className="block text-white font-semibold mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#4A90E2] transition-colors resize-none"
                    placeholder="How can we help?"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-4 rounded-lg font-semibold transition-all transform hover:scale-105"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-heading font-bold text-white mb-6">Other ways to reach us</h2>
                
                <div className="space-y-6">
                  <ContactMethod 
                    icon={<Mail className="h-6 w-6" />}
                    title="Email Support"
                    content="support@wingletapp.com"
                    description="For general questions and support requests"
                  />
                  
                  <ContactMethod 
                    icon={<Clock className="h-6 w-6" />}
                    title="Response Time"
                    content="24-48 hours"
                    description="We aim to respond within 1-2 business days"
                  />
                  
                  <ContactMethod 
                    icon={<MessageSquare className="h-6 w-6" />}
                    title="Live Chat (Coming Soon)"
                    content="In-app support"
                    description="Direct support inside Winglet for logged-in users"
                  />
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-6">
                <h3 className="text-xl font-heading font-bold text-white mb-4">Quick Links</h3>
                <div className="space-y-3">
                  <QuickLink to="/how-it-works" text="How Winglet Works" />
                  <QuickLink to="/pricing" text="Pricing & FAQ" />
                  <QuickLink to="/compare-tools" text="Compare to Other Tools" />
                  <QuickLink to="/privacy" text="Privacy Policy" />
                  <QuickLink to="/terms" text="Terms of Service" />
                </div>
              </div>

              {/* Support Hours */}
              <div className="bg-gradient-to-br from-[#4A90E2]/20 to-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-6">
                <h3 className="text-xl font-heading font-bold text-white mb-3">Support Hours</h3>
                <p className="text-gray-300 mb-4">
                  We're a small team, but we're committed to helping every pilot. Email us anytime—we'll respond within 24-48 hours.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-300">
                    <span>Monday - Friday:</span>
                    <span className="text-white font-semibold">9 AM - 6 PM EST</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Saturday - Sunday:</span>
                    <span className="text-white font-semibold">Closed (email only)</span>
                  </div>
                </div>
                <p className="text-gray-400 text-xs mt-4">
                  Urgent FAR 117 or reroute questions? Mark subject as "URGENT" and we'll prioritize.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            Common Questions
          </h2>
          
          <div className="space-y-4">
            <FAQItem 
              question="How do I report a bug?"
              answer="Use the form above and select 'Bug Report' as the subject. Include your rotation ID if applicable, and describe what you expected vs what happened. Screenshots help!"
            />
            <FAQItem 
              question="Can you help me with a grievance?"
              answer="Absolutely. Select 'Grievance Assistance' and include your rotation number. We'll review the calculation and provide an audit trail with PWA citations you can use with your union rep."
            />
            <FAQItem 
              question="I have a feature request—do you take suggestions?"
              answer="Yes! We're pilots too, and we know what we need. Select 'Feature Request' and tell us what would make Winglet better for you."
            />
            <FAQItem 
              question="My company timecard doesn't match Winglet. What now?"
              answer="This is exactly what Winglet is for. Email us with both your rotation number and timecard details. We'll verify our calculation and help you file a grievance if needed."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
            </div>
            
            {/* Product Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
              </ul>
            </div>
            
            {/* Company Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const ContactMethod = ({ icon, title, content, description }) => (
  <div className="flex items-start space-x-4">
    <div className="flex-shrink-0 w-12 h-12 bg-[#4A90E2]/20 rounded-lg flex items-center justify-center text-[#4A90E2]">
      {icon}
    </div>
    <div>
      <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
      <p className="text-[#4A90E2] font-semibold mb-1">{content}</p>
      <p className="text-gray-400 text-sm">{description}</p>
    </div>
  </div>
);

const QuickLink = ({ to, text }) => (
  <Link 
    to={to} 
    className="flex items-center text-gray-300 hover:text-[#4A90E2] transition-colors group"
  >
    <span className="w-2 h-2 bg-[#4A90E2]/50 rounded-full mr-3 group-hover:bg-[#4A90E2] transition-colors"></span>
    {text}
  </Link>
);

const FAQItem = ({ question, answer }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-6">
    <h3 className="text-lg font-semibold text-white mb-2">{question}</h3>
    <p className="text-gray-300">{answer}</p>
  </div>
);

export default ContactPage;
